import { useState, useRef } from "react";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Image as ImgIcon, FileText, Loader2 } from "lucide-react";

export function ImageUpload({ value, onChange, label = "Cover Image" }) {
    const [busy, setBusy] = useState(false);
    const ref = useRef();

    const upload = async (file) => {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/upload?kind=image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            onChange(data.url);
            toast.success("আপলোড সফল");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const fullUrl = value && value.startsWith("/") ? `${BACKEND_URL}${value}` : value;

    return (
        <div>
            <label className="block text-xs mb-1 font-medium">{label}</label>
            <div className="flex items-start gap-3">
                <div onClick={() => ref.current?.click()} className="relative w-28 h-32 rounded-lg border-2 border-dashed border-brand-light bg-brand-light/20 hover:bg-brand-light/40 cursor-pointer overflow-hidden grid place-items-center group">
                    {value ? (
                        <>
                            <img src={fullUrl} alt="" className="w-full h-full object-cover" />
                            <button type="button" onClick={(e) => { e.stopPropagation(); onChange(""); }} className="absolute top-1 right-1 bg-white/95 text-brand-red rounded-full p-1 shadow"><X className="w-3 h-3" /></button>
                        </>
                    ) : busy ? <Loader2 className="w-6 h-6 animate-spin text-brand-royal" /> : (
                        <div className="text-center text-xs text-brand-navy/60 px-2">
                            <ImgIcon className="w-6 h-6 mx-auto mb-1 text-brand-royal/60" />
                            ক্লিক করে আপলোড
                        </div>
                    )}
                </div>
                <div className="flex-1">
                    <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="image-upload-input" />
                    <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="অথবা URL পেস্ট করুন" className="w-full px-3 py-2 text-xs rounded-lg border border-brand-light bg-white" />
                    <p className="text-[10px] text-brand-navy/50 mt-1">JPG/PNG/WebP, সর্বোচ্চ ১০MB</p>
                </div>
            </div>
        </div>
    );
}

export function GalleryUpload({ value = [], onChange }) {
    const [busy, setBusy] = useState(false);
    const ref = useRef();

    const uploadMany = async (files) => {
        if (!files?.length) return;
        setBusy(true);
        try {
            const urls = [];
            for (const file of files) {
                const fd = new FormData();
                fd.append("file", file);
                const { data } = await api.post("/admin/upload?kind=image", fd, { headers: { "Content-Type": "multipart/form-data" } });
                urls.push(data.url);
            }
            onChange([...value, ...urls]);
            toast.success(`${urls.length} টি ছবি আপলোড হয়েছে`);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
    const fullUrl = (u) => u?.startsWith("/") ? `${BACKEND_URL}${u}` : u;

    return (
        <div>
            <label className="block text-xs mb-1 font-medium">Gallery Images (Multiple)</label>
            <div className="flex flex-wrap gap-2">
                {value.map((u, i) => (
                    <div key={i} className="relative w-20 h-24 rounded-lg overflow-hidden border border-brand-light">
                        <img src={fullUrl(u)} alt="" className="w-full h-full object-cover" />
                        <button type="button" onClick={() => remove(i)} className="absolute top-0.5 right-0.5 bg-white/95 text-brand-red rounded-full p-0.5"><X className="w-3 h-3" /></button>
                    </div>
                ))}
                <div onClick={() => ref.current?.click()} className="w-20 h-24 rounded-lg border-2 border-dashed border-brand-light hover:bg-brand-light/40 cursor-pointer grid place-items-center text-brand-royal">
                    {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                </div>
                <input ref={ref} type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadMany(Array.from(e.target.files || []))} data-testid="gallery-upload-input" />
            </div>
        </div>
    );
}

export function PdfUpload({ value, onChange }) {
    const [busy, setBusy] = useState(false);
    const ref = useRef();

    const upload = async (file) => {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/upload?kind=pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
            onChange(data.url);
            toast.success("PDF আপলোড সফল");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    return (
        <div>
            <label className="block text-xs mb-1 font-medium">Sample PDF (E-Book preview)</label>
            <div className="flex items-center gap-3">
                <button type="button" onClick={() => ref.current?.click()} className="px-4 py-2 rounded-lg border border-brand-light hover:bg-brand-light/40 inline-flex items-center gap-2 text-sm">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4 text-brand-red" />}
                    {value ? "Replace PDF" : "Upload PDF"}
                </button>
                {value && <button type="button" onClick={() => onChange("")} className="text-brand-red text-xs"><X className="w-3.5 h-3.5 inline" /> Remove</button>}
                <input ref={ref} type="file" accept="application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="pdf-upload-input" />
            </div>
            {value && <p className="text-[11px] text-brand-royal mt-1 break-all">📄 {value}</p>}
        </div>
    );
}
